import { useId } from "react";

/** Рубашка карты: коричневый фон, тонкие золотые линии, звезда в круге. */
export function CardBack({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 100 160" preserveAspectRatio="xMidYMid slice" aria-hidden>
      <rect width="100" height="160" rx="8" fill="#6f4229" />
      <rect x="4" y="4" width="92" height="152" rx="5" fill="none" stroke="rgba(217,180,138,.45)" strokeWidth=".8" />
      <g stroke="rgba(217,180,138,.4)" strokeWidth=".6" fill="none">
        <path d="M50 12L88 60L50 148L12 60Z" />
        <path d="M50 24L76 60L50 132L24 60Z" />
        <path d="M12 60H88M50 12V148" />
        <path d="M4 30H96M4 130H96" />
      </g>
      <circle cx="50" cy="80" r="9" fill="#6f4229" stroke="rgba(217,180,138,.85)" strokeWidth=".9" />
      <path
        d="M50 74l1.9 4.1 4.4.5-3.3 3 .9 4.4L50 83.9l-3.9 2.1.9-4.4-3.3-3 4.4-.5z"
        fill="none"
        stroke="rgba(217,180,138,.9)"
        strokeWidth=".7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const ROMAN: [number, string][] = [[10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
export function roman(n: number): string {
  let out = "";
  let rest = n;
  for (const [v, s] of ROMAN) {
    while (rest >= v) {
      out += s;
      rest -= v;
    }
  }
  return out;
}

/** Переносит длинное название карты на две строки (по пробелу или дефису). */
function wrapName(name: string): string[] {
  if (name.length <= 12) return [name];
  const parts = name.split(/(?<=-)|\s+/u).filter(Boolean);
  if (parts.length < 2) return [name];
  let best = [name];
  let bestMax = Infinity;
  for (let i = 1; i < parts.length; i++) {
    const a = parts.slice(0, i).join(" ").replace(/- /gu, "-");
    const b = parts.slice(i).join(" ");
    const m = Math.max(a.length, b.length);
    if (m < bestMax) {
      best = [a, b];
      bestMax = m;
    }
  }
  return best;
}

/** Лицо карты: красная рамка, «картинка» из геометрии по номеру, римская цифра и название. */
export function CardFace({ n, name, className }: { n: number; name: string; className?: string }) {
  const uid = useId().replace(/:/gu, "");
  const sides = 3 + (n % 6);
  const rings = 3 + (n % 4);
  const rot = (n * 37) % 90;
  const poly = (r: number, offset: number) =>
    Array.from({ length: sides }, (_, i) => {
      const a = ((360 / sides) * i + offset - 90) * (Math.PI / 180);
      return `${(100 + r * Math.cos(a)).toFixed(1)},${(120 + r * Math.sin(a)).toFixed(1)}`;
    }).join(" ");
  const hue = 8 + ((n * 23) % 40);
  const lines = wrapName(name);
  const longest = Math.max(...lines.map((l) => l.length));
  const fontSize = longest <= 10 ? 20 : longest <= 14 ? 15 : 12;
  return (
    <svg className={className} viewBox="0 0 200 320" aria-label={`Карта ${n}: ${name}`}>
      <defs>
        <radialGradient id={`g${uid}`} cx="50%" cy="38%" r="70%">
          <stop offset="0" stopColor={`hsl(${hue} 70% 32%)`} />
          <stop offset="1" stopColor="#140708" />
        </radialGradient>
      </defs>
      <rect width="200" height="320" rx="16" fill="#b3271c" />
      <rect x="9" y="9" width="182" height="302" rx="11" fill={`url(#g${uid})`} />
      <path d="M70 9c8 14 52 14 60 0" fill="#b3271c" />
      <g fill="none" stroke="rgba(245,225,205,.75)" strokeWidth="1.2">
        {Array.from({ length: rings }, (_, i) => (
          <polygon key={i} points={poly(74 - i * 13, rot + i * (180 / sides))} opacity={1 - i * 0.16} />
        ))}
        <circle cx="100" cy="120" r="6" fill="rgba(245,225,205,.8)" />
      </g>
      <g transform="translate(100 226)">
        <polygon points="0,-20 17,-10 17,10 0,20 -17,10 -17,-10" fill="#b3271c" stroke="#f5ece6" strokeWidth="1.5" />
        <text y="1" textAnchor="middle" dominantBaseline="central" fill="#f5ece6" fontFamily="Georgia, serif" fontSize="15">
          {roman(n)}
        </text>
      </g>
      {lines.map((line, i) => (
        <text
          key={i}
          x="100"
          y={lines.length === 1 ? 282 : 268 + i * 20}
          textAnchor="middle"
          fill="#f5ece6"
          fontFamily="Georgia, serif"
          fontSize={fontSize}
          letterSpacing="1"
        >
          {line.toUpperCase()}
        </text>
      ))}
    </svg>
  );
}

/** Веер из пяти рубашек (карточка «Вытяни карту дня»). */
export function Fan() {
  const cards = [-28, -14, 0, 14, 28];
  return (
    <svg className="fan" viewBox="0 0 320 150" aria-hidden>
      {cards.map((a, i) => (
        <g key={a} transform={`translate(160 230) rotate(${a})`} opacity={i === 2 ? 1 : 0.92}>
          <g transform="translate(-42 -215)">
            <rect width="84" height="132" rx="7" fill="#f7eee8" stroke="rgba(166,124,101,.55)" strokeWidth="1.2" />
            <rect x="5" y="5" width="74" height="122" rx="5" fill="none" stroke="rgba(166,124,101,.35)" />
            <path d="M42 14L72 52L42 120L12 52Z" fill="none" stroke="rgba(166,124,101,.35)" />
            <circle cx="42" cy="66" r="7" fill="none" stroke="rgba(166,124,101,.7)" />
          </g>
        </g>
      ))}
    </svg>
  );
}

/** Узор «звезда судьбы» для карточки-приглашения. */
export function StarPattern() {
  return (
    <svg className="pattern" viewBox="0 0 300 300" preserveAspectRatio="xMidYMid slice" aria-hidden>
      <g fill="none" stroke="rgba(255,235,222,.7)" strokeWidth="1.2">
        <path d="M150 20L200 120L290 150L200 180L150 280L100 180L10 150L100 120Z" />
        <path d="M150 60L180 130L250 150L180 170L150 240L120 170L50 150L120 130Z" />
        <path d="M20 60L150 150L280 60M20 240L150 150L280 240" />
        <circle cx="150" cy="150" r="130" />
        <circle cx="150" cy="150" r="70" />
      </g>
    </svg>
  );
}

/** Три рубашки веером — иконка расклада на три карты. */
export function ThreeBacks() {
  return (
    <svg viewBox="0 0 150 130" aria-hidden>
      {[-16, 0, 16].map((a) => (
        <g key={a} transform={`translate(75 120) rotate(${a})`}>
          <g transform="translate(-30 -112)">
            <rect width="60" height="96" rx="6" fill="#6f4229" stroke="rgba(217,180,138,.7)" strokeWidth="1" />
            <path d="M30 10L52 38L30 86L8 38Z" fill="none" stroke="rgba(217,180,138,.55)" />
            <circle cx="30" cy="48" r="6" fill="none" stroke="rgba(217,180,138,.85)" />
          </g>
        </g>
      ))}
    </svg>
  );
}

export function OneBack() {
  return (
    <svg viewBox="0 0 100 130" aria-hidden>
      <g transform="translate(20 6)">
        <rect width="60" height="96" rx="6" fill="#6f4229" stroke="rgba(217,180,138,.7)" strokeWidth="1" />
        <path d="M30 10L52 38L30 86L8 38Z" fill="none" stroke="rgba(217,180,138,.55)" />
        <circle cx="30" cy="48" r="6" fill="none" stroke="rgba(217,180,138,.85)" />
      </g>
    </svg>
  );
}
